import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AccountPaymentMethodAddApiElementContentModel,
  AccountPaymentMethodAddApiPropertiesModel,
  IApiContentResponseModel,
  PageSettingsPropertiesModel,
} from '@/services/platform/api/model';
import { cn, ModuleProperties, useLocaleStore } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { setPreferredPaymentMethod } from '@/services/platform/data/payment-methods';
import { ProfilePaymentAdd } from '@/components/shared/profile/payment/add';

/**
 * The "Add payment method" page (`/{locale}/profile/payment/add-payment-method/`).
 *
 * The list screen normally opens the same form as an in-place overlay; this module serves the
 * standalone CMS page, so on success it returns to the payment methods list by route.
 */
export const AccountPaymentMethodAddApi = (props: ModuleProperties) => {
  const content = props.content as AccountPaymentMethodAddApiElementContentModel | undefined;
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const setHideNavbar = useUIStore(state => state.setHideNavbar);

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  const page = props.context?.page as IApiContentResponseModel | undefined;
  const pageProperties = page?.properties as PageSettingsPropertiesModel | undefined;
  const properties = content?.properties as AccountPaymentMethodAddApiPropertiesModel | undefined;

  const language = locale?.split('-')[0] ?? 'en';
  const listPath = `/${language}/profile/payment/`;

  if (!content) return null;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__account-payment-method-add-api']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      {/*
        A fixed `height` rather than `minHeight`: the form is `flex h-full flex-col` with a
        scrolling body and a `shrink-0` footer, so it needs a bounded parent for `h-full` to
        resolve — with only a minimum, the footer is pushed below the content and the whole page
        scrolls instead of just the form. 60px is the header `DefaultScreenLayout` reserves.
      */}
      <div
        className="flex flex-col"
        style={{
          height:
            'calc(100dvh - 60px - max(1rem, env(safe-area-inset-top)) - max(1rem, env(safe-area-inset-bottom)))',
        }}
      >
        <ProfilePaymentAdd
          properties={properties}
          title={pageProperties?.pageName ?? ''}
          // `DefaultScreenLayout` already renders a fixed header for this page.
          hideHeader
          // `replace` on every exit: the list pushes this page, so pushing the list back would
          // stack a second list entry and leave the add page below it — the list's own arrow
          // (`DefaultScreenLayout`'s `navigate(-1)`) would then return here, looping forever.
          onBack={() => navigate(listPath, { replace: true })}
          // The toast lives on the list screen, so the outcome travels back as router state.
          onDiscarded={() => navigate(listPath, { replace: true, state: { paymentToast: 'discarded' } })}
          onSaved={async (paymentMethodId, isPreferred) => {
            // The card is already attached at this point, so a failure to set the preference must
            // not read as a failed save — the list still reports the card as added.
            if (isPreferred) await setPreferredPaymentMethod(paymentMethodId);
            navigate(listPath, { replace: true, state: { paymentToast: 'added' } });
          }}
        />
      </div>
    </ModuleWrapper>
  );
};
